import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, Collapse, Divider, FormControlLabel, IconButton, Link,
  MenuItem, Paper, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';

import { DurationField } from './DurationField';
import { SitesModeSwitch, type SitesMode } from './SitesModeSwitch';
import { PinChips } from './PlannerForm';
import { BreakFields, BREAK_CATEGORIES } from './BreakFields';
import { FieldGroup } from './FieldGroup';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';

import { PlaceAutocomplete } from './PlaceAutocomplete';
import { PlacePicker } from './PlacePicker';
import { StayRows } from './StayRows';
import { AvoidancePanel, BoostChoice, CategoryChoice } from './AvoidancePanel';
import {
  StopRecommendations, useRecommendations, type MapPreview,
} from './StopRecommendations';
import type { Category, CoordStop, PlanRequest, Poi, RouteStyle, TravelMode } from '../api/types';
import { styleName } from '../copy/mission';
import { SELECTABLE_TRAVEL_MODES, MODE_LABELS } from '../api/types';

/**
 * One day of a trip, as the form holds it.
 *
 * Everything here is an override, and every question the single-day screen
 * asks is asked again — where to go, what to see, when to eat, what to keep
 * clear of — using the same components, so the two screens are learned once.
 * `'inherit'` on a break and empty lists elsewhere are what let a day say
 * nothing at all and simply follow the trip.
 */
export interface DayDraft {
  date: string;
  /** True once the traveller has moved this date by hand. */
  dateTouched: boolean;
  start: Poi | null;
  end: Poi | null;
  mode: TravelMode | null;
  /**
   * Replaces the trip's styles for this day. Empty inherits them.
   *
   * This is how a day is given a character of its own — a food-led Tuesday
   * among culture-led days. The API also takes a per-day `themeKey`, which
   * reserves a share of the stops rather than steering the ranking; it is not
   * offered here because two controls for "what is this day about" is one
   * more than anybody needs.
   */
  styleKeys: string[];
  interests: string[];
  /**
   * Whether this day's sites are decided by kind or named outright.
   *
   * Held on the DAY rather than in the card, because the request for a day is
   * built in the trip form from these fields — a mode kept as component state
   * would style the controls and change nothing that is sent.
   *
   * Absent on a day restored from a saved trip written before this existed,
   * so every read defaults rather than trusting it.
   */
  sitesMode?: 'kinds' | 'named';
  /** Which of those to favor, once there are enough to choose between. */
  boostKeys: string[];
  /** Stay time per kind of site, this day only. */
  categoryDwell: Record<string, number>;
  /** Places asked for by name, or chosen from this day's recommendations. */
  mustVisit: Poi[];
  /**
   * Stay times for this day's places, keyed by place id.
   *
   * Merged over the trip's rather than replacing them: these are per PLACE,
   * so a day naming one says nothing about the others — unlike interests or
   * avoidance, where a day's list is the whole of what it wants.
   */
  stopDwellMinutes: Record<string, number>;
  availableMinutes: number | null;

  lunch: 'inherit' | 'on' | 'off';
  lunchAt: string;
  lunchMinutes: number;
  lunchPlace: Poi | null;
  /*
    Whether the hour is a constraint, and who picks the place.

    The same two choices the one-day form offers, held on the DAY for the
    same reason `sitesMode` is: the request for a day is built in the trip
    form from these fields, so a mode kept as component state would style
    the controls and change nothing that is sent.

    Both optional, so a day restored from a trip saved before they existed
    reads as the default rather than as `undefined`.
  */
  lunchPinned?: boolean;
  lunchMode?: SitesMode;

  coffee: 'inherit' | 'on' | 'off';
  coffeeAt: string;
  coffeeMinutes: number;
  coffeePlace: Poi | null;
  coffeePinned?: boolean;
  coffeeMode?: SitesMode;

  /** Empty means "as the trip"; anything here replaces it for this day. */
  avoid: Poi[];
  avoidCategories: string[];
  avoidDistanceM: number;
  /** Per-place and per-category radii for this day, each beating the slider. */
  avoidDistanceByPoiId: Record<string, number>;
  avoidDistanceByCategoryKey: Record<string, number>;

  skip: boolean;
}

export function blankDay(date: string): DayDraft {
  return {
    date, dateTouched: false, start: null, end: null, mode: null,
    styleKeys: [], interests: [], boostKeys: [], categoryDwell: {},
    sitesMode: 'kinds',
    mustVisit: [], stopDwellMinutes: {},
    availableMinutes: null,
    lunch: 'inherit', lunchAt: '13:00', lunchMinutes: 45, lunchPlace: null,
    lunchPinned: false, lunchMode: 'kinds',
    coffee: 'inherit', coffeeAt: '16:00', coffeeMinutes: 20, coffeePlace: null,
    coffeePinned: false, coffeeMode: 'kinds',
    avoid: [], avoidCategories: [], avoidDistanceM: 500,
    avoidDistanceByPoiId: {}, avoidDistanceByCategoryKey: {},
    skip: false,
  };
}

/** Minutes from the day's departure to a wall-clock time. */
function minutesFrom(startTime: string, at: string): number {
  const [sh, sm] = startTime.split(':').map(Number);
  const [ah, am] = at.split(':').map(Number);
  return Math.max(0, (ah! * 60 + am!) - (sh! * 60 + sm!));
}

/** A tri-state that can say "not on this day" as well as "yes" and "as usual". */
function BreakChoice({ value, onChange, inherited }: {
  value: 'inherit' | 'on' | 'off';
  onChange: (next: 'inherit' | 'on' | 'off') => void;
  /** What inheriting actually means, so the middle option is not a mystery. */
  inherited: boolean;
}) {
  return (
    <ToggleButtonGroup
      exclusive size="small" value={value}
      onChange={(_, next) => next && onChange(next)}
    >
      <ToggleButton value="inherit" sx={{ py: 0.25, px: 1, fontSize: '0.7rem' }}>
        {inherited ? 'As trip (yes)' : 'As trip (no)'}
      </ToggleButton>
      <ToggleButton value="on" sx={{ py: 0.25, px: 1.25, fontSize: '0.7rem' }}>Yes</ToggleButton>
      <ToggleButton value="off" sx={{ py: 0.25, px: 1.25, fontSize: '0.7rem' }}>No</ToggleButton>
    </ToggleButtonGroup>
  );
}

export function TripDayCard({
  index, day, cityId, styles, categories, removable,
  tripStyleKeys, tripLunch, tripCoffee, tripMode, tripStartTime,
  tripAvoidCount, tripAvailableMinutes, tripInterests, markedStops, onMarkedStopsChange,
  markedEnds, onMarkedEndChange,
  previousEnd, tripBase, tripFinish, colourFor, onShowDetails, onSuggestionPreview,
  onBrowsePlaces, onChange, onRemove,
}: {
  index: number;
  day: DayDraft;
  cityId: string;
  styles: RouteStyle[];
  categories: Category[];
  removable: boolean;
  /**
   * The trip's styles, which supply this day's categories when it names none
   * of its own — so the suggestions a day shows are drawn from the same
   * categories its plan will be.
   */
  tripStyleKeys: string[];
  /** The operation's own kinds of site, which a day inherits when it sets none. */
  tripInterests: string[];
  /**
   * Spots marked on the map for this day, which have no place record.
   *
   * Listed here as well as drawn there, because a stop that exists only on
   * the map is one the form silently disagrees with — the day reads as
   * having nothing of its own while the route visits somewhere.
   */
  markedStops?: CoordStop[];
  onMarkedStopsChange?: (points: CoordStop[]) => void;
  /**
   * This day's start and finish marked on the map, which beat the pickers.
   *
   * Shown here as well as drawn there, or the field goes on reading
   * "Inherited" while the day in fact starts somewhere of its own — the form
   * saying one thing and the map another.
   */
  markedEnds?: { start?: CoordStop; end?: CoordStop };
  onMarkedEndChange?: (role: 'start' | 'end', pin: CoordStop | null) => void;
  tripLunch: boolean;
  tripCoffee: boolean;
  tripMode: TravelMode;
  tripStartTime: string;
  tripAvoidCount: number;
  tripAvailableMinutes: number;
  /** Where the previous day ends, for the placeholder on this day's start. */
  previousEnd: Poi | null;
  tripBase: Poi | null;
  /**
   * The trip's objective, where it has one. A day that names neither end of
   * itself finishes here rather than back at the base — the same order the
   * server resolves endpoints in.
   */
  tripFinish: Poi | null;
  colourFor: (categoryKey: string) => string;
  onShowDetails: (poiId: string, role?: 'visit' | 'lunch' | 'coffee') => void;
  /**
   * What this day's suggestions want drawn, reported up so the map can draw
   * it. Only one panel is ever being read at a time, so the last to report
   * wins — which is the behaviour anybody would expect and the hardest to get
   * by wiring each card to the map independently.
   */
  onSuggestionPreview?: (preview: MapPreview) => void;
  /**
   * Opens the catalogue for THIS day: its stops, its start, its finish.
   *
   * A day of an operation is a round in its own right, and the question
   * asked of it is the single-day screen's question — of the places that
   * would do, which has already been worked, and by whom. Asked per day
   * rather than once for the operation because the answer differs: ground
   * covered on Tuesday is exactly the ground Wednesday should leave alone.
   */
  onBrowsePlaces?: () => void;
  onChange: (patch: Partial<DayDraft>) => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [avoidFilter, setAvoidFilter] = useState('');

  const visitable = categories.filter((c) => c.isVisitable);
  /* What this day will actually start and end at, under the inheritance rule. */
  const effectiveStart = day.start ?? previousEnd ?? tripBase;
  const effectiveEnd = day.end ?? (day.start ?? tripFinish ?? tripBase);

  /*
    Whether a time budget has anything to decide on this day.

    A day's own kinds, else whatever it inherits: the operation's kinds, its
    styles, or its own. Mirrors `budgetDecides` in the one-day form.
  */
  const budgetDecides = day.interests.length > 0
    || day.styleKeys.length > 0
    || tripInterests.length > 0
    || tripStyleKeys.length > 0;

  const lunchOn = day.lunch === 'on' || (day.lunch === 'inherit' && tripLunch);
  const coffeeOn = day.coffee === 'on' || (day.coffee === 'inherit' && tripCoffee);

  /**
   * The request this day's suggestions are ranked against.
   *
   * Built from the same inheritance the server applies, so what a traveller
   * is shown here is what the day will actually be built from. Null until
   * there is somewhere to start, which is exactly when the panel has nothing
   * to say.
   */
  const draft = useMemo<PlanRequest | null>(() => {
    if (!effectiveStart || !effectiveEnd) return null;
    const ref = (p: Poi) => ({ kind: 'poi' as const, poiId: p.id });

    return {
      cityId,
      start: ref(effectiveStart),
      end: ref(effectiveEnd),
      mode: day.mode ?? tripMode,
      date: day.date,
      availableMinutes: day.availableMinutes ?? tripAvailableMinutes,
      mustVisitPoiIds: [],
      interestCategoryKeys: day.interests,
      boostCategoryKeys: day.boostKeys.filter((key) => day.interests.includes(key)),
      dwellMinutesByCategoryKey: day.categoryDwell,
      /*
       * This day's own styles if it has any, otherwise the trip's — the same
       * order the server resolves them in, so what is suggested here is what
       * the day will actually be built from.
       */
      ...(day.interests.length === 0 && (day.styleKeys.length > 0 || tripStyleKeys.length > 0)
        ? { routeStyleKeys: day.styleKeys.length > 0 ? day.styleKeys : tripStyleKeys }
        : {}),
      avoid: {
        poiIds: day.avoid.map((p) => p.id),
        categoryKeys: day.avoidCategories,
      },
      safetyPreference: 'off',
      // Carried so the break lists have something to rank for.
      ...(lunchOn
        ? {
            lunch: {
              targetMinutesFromStart: minutesFrom(tripStartTime, day.lunchAt),
              durationMinutes: day.lunchMinutes,
              flexibilityMinutes: 60,
            },
          }
        : {}),
      ...(coffeeOn
        ? {
            coffee: {
              targetMinutesFromStart: minutesFrom(tripStartTime, day.coffeeAt),
              durationMinutes: day.coffeeMinutes,
              flexibilityMinutes: 60,
            },
          }
        : {}),
    };
  }, [
    cityId, effectiveStart, effectiveEnd, day, tripMode, tripStyleKeys,
    lunchOn, coffeeOn, tripAvailableMinutes, tripStartTime,
  ]);

  // Whether this day's "name a place yourself" controls are open.
  const [alsoNaming, setAlsoNaming] = useState(false);
  const showNaming = (day.sitesMode ?? 'kinds') === 'named'
    || alsoNaming
    || day.mustVisit.length > 0;

  const recommendations = useRecommendations(draft);

  useEffect(() => {
    onSuggestionPreview?.(recommendations.preview);
  }, [recommendations.preview, onSuggestionPreview]);

  const changed = day.start || day.end || day.mode || day.styleKeys.length > 0
    || day.interests.length > 0 || day.mustVisit.length > 0 || day.avoid.length > 0
    || day.avoidCategories.length > 0 || day.lunch !== 'inherit' || day.coffee !== 'inherit';

  return (
    <Paper variant="outlined" sx={{ p: 1.5, opacity: day.skip ? 0.6 : 1 }}>
      {/*
        ── the row that is always visible ─────────────────────────
        The label and the three controls are fixed; the date takes whatever is
        left. Sized the other way round — a fixed date and a flexible gap —
        the row overran the card and pushed the delete button past its edge.
      */}
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0, mr: 0.5 }}>
          Day {index + 1}
        </Typography>
        <TextField
          size="small" type="date" value={day.date}
          onChange={(e) => onChange({ date: e.target.value, dateTouched: true })}
          sx={{ flex: 1, minWidth: 132 }}
        />

        <Tooltip title={day.skip ? 'Plan this day' : 'Leave this day free'} arrow>
          <Button
            size="small"
            sx={{ flexShrink: 0, minWidth: 0, px: 0.75 }}
            onClick={() => onChange({ skip: !day.skip })}
          >
            {day.skip ? 'Plan it' : 'Free'}
          </Button>
        </Tooltip>
        <Tooltip title={open ? 'Done' : 'Change this day'} arrow>
          <IconButton size="small" sx={{ flexShrink: 0 }} onClick={() => setOpen((o) => !o)}>
            {open ? <ExpandMoreIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
              : <TuneIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        {removable && (
          <Tooltip title={`Remove day ${index + 1}`} arrow>
            <IconButton size="small" sx={{ flexShrink: 0 }} onClick={onRemove}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/*
        What this day has been given, on its own line.
        Sharing the row above cost it a gap it did not have to spare, and
        chips are the one thing here that can afford to sit below.
      */}
      {(day.mustVisit.length > 0 || day.styleKeys.length > 0 || (changed && !open)) && (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.75 }} useFlexGap>
          {day.mustVisit.length > 0 && (
            <Chip size="small" color="primary" variant="outlined"
              label={`${day.mustVisit.length} to visit`} />
          )}
          {day.styleKeys.length > 0 && (
            <Chip size="small" variant="outlined"
              label={styles.find((st) => st.key === day.styleKeys[0])?.label
                ?? day.styleKeys[0]} />
          )}
          {changed && !open && day.styleKeys.length === 0 && day.mustVisit.length === 0 && (
            <Chip size="small" color="info" variant="outlined" label="Changed" />
          )}
        </Stack>
      )}

      {/* ── everything this day may override ─────────────────────── */}
      <Collapse in={open && !day.skip} unmountOnExit>
        <Stack spacing={2} sx={{ mt: 1.5 }}>
          <Divider />

          <Stack spacing={1.5}>
            <PlaceAutocomplete
              cityId={cityId}
              allCities
              label="Starting at"
              placeholder={previousEnd?.name ?? tripBase?.name ?? 'Where you are staying'}
              value={day.start}
              onChange={(poi) => onChange({ start: poi })}
              helperText={markedEnds?.start
                ? 'Marked on the map — clear it below to use this'
                : day.start ? 'This day only' : 'Inherited'}
            />
            {markedEnds?.start && onMarkedEndChange && (
              <PinChips
                pins={[markedEnds.start]}
                onChange={() => onMarkedEndChange('start', null)}
                colour="primary"
              />
            )}
            <PlaceAutocomplete
              cityId={cityId}
              allCities
              label="Destination"
              placeholder={effectiveEnd?.name ?? 'Back where it started'}
              value={day.end}
              onChange={(poi) => onChange({ end: poi })}
              helperText={markedEnds?.end
                ? 'Marked on the map — clear it below to use this'
                : day.end ? 'The next day starts here' : 'Inherited'}
            />
            {markedEnds?.end && onMarkedEndChange && (
              <PinChips
                pins={[markedEnds.end]}
                onChange={() => onMarkedEndChange('end', null)}
                colour="primary"
              />
            )}
          </Stack>

          <Stack spacing={1.5}>
            <TextField
              size="small" select fullWidth label="Transport" value={day.mode ?? ''}
              onChange={(e) => onChange({ mode: (e.target.value || null) as TravelMode | null })}
            >
              <MenuItem value="">As trip ({MODE_LABELS[tripMode]})</MenuItem>
              {SELECTABLE_TRAVEL_MODES.map((m) => (
                <MenuItem key={m} value={m}>{MODE_LABELS[m]}</MenuItem>
              ))}
            </TextField>
            {/*
              A day's own style replaces the trip's for that day, so a
              food-led Tuesday needs nothing said about the other six.
            */}
            <Autocomplete
              size="small"
              options={styles.map((st) => st.key)}
              value={day.styleKeys[0] ?? null}
              onChange={(_, next) => onChange({ styleKeys: next ? [next] : [] })}
              getOptionLabel={(key) => {
                const style = styles.find((st) => st.key === key);
                return style ? styleName(style.key, style.label) : key;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Emphasis, this day only"
                  placeholder={day.styleKeys.length === 0 ? 'As the trip' : ''}
                  helperText={day.styleKeys.length > 0 ? 'Replaces the trip’s' : 'Inherited'}
                />
              )}
            />
          </Stack>

          {/*
            Folding sections, by the same names the one-day form uses.

            These were plain headings with every section open at once, inside
            a card that is itself one of seven on screen — so opening a day
            buried the next six, and the two forms asked one question under
            two different titles ("Places to visit" against "Sites to visit").

            Shut to begin with: a day wanting nothing of its own is the common
            case, and the tag on each heading says when there IS something
            inside, so nothing has to be opened to find out.
          */}
          <FieldGroup
            title="Sites to visit"
            caption="What should this day actually stop at?"
            // Spots marked on the map count as much as places named in the
            // list: the tag is what a shut panel says about what is inside it,
            // and a day holding a marked stop was reading as empty.
            count={day.interests.length + day.mustVisit.length + (markedStops?.length ?? 0)}
          >
            <Stack spacing={1.5}>
              {/*
                A day's own kinds of site replace the operation's for this
                day. "Schools on Tuesday" means schools on Tuesday, not
                schools as well as everything the style already asks for.

                One control, at the top of the list it governs: this was a
                filter here and a separate interests box further down, which
                were two fields for what an operator reads as one question.
              */}
              <SitesModeSwitch
                mode={(day.sitesMode ?? 'kinds')}
                onChange={(next) => onChange({ sitesMode: next })}
              />

              {(day.sitesMode ?? 'kinds') === 'kinds' && (<>
              <CategoryChoice
                categories={visitable}
                value={day.interests}
                onChange={(keys) => onChange({
                  interests: keys,
                  categoryDwell: Object.fromEntries(
                    Object.entries(day.categoryDwell).filter(([key]) => keys.includes(key))),
                })}
                dwellMinutes={day.categoryDwell}
                onDwellChange={(key, minutes) => {
                  const next = { ...day.categoryDwell };
                  if (minutes === null) delete next[key];
                  else next[key] = minutes;
                  onChange({ categoryDwell: next });
                }}
                label="Kinds of site, this day only"
                helperText={day.interests.length > 0
                  ? 'This day draws from these, and the search below is narrowed to them.'
                  : 'From the operation’s own kinds, or its route style.'}
              />
              <BoostChoice
                categories={visitable}
                chosen={day.interests}
                value={day.boostKeys}
                onChange={(keys) => onChange({ boostKeys: keys })}
              />
              </>)}

              {/*
                Naming a place works in both modes, as on the single day, and
                is offered once this day has a kind to add to.
              */}
              {(day.sitesMode ?? 'kinds') === 'kinds' && day.interests.length > 0 && (
                <FormControlLabel
                  sx={{ m: 0, mt: 0.5 }}
                  control={(
                    <Switch
                      size="small"
                      checked={showNaming}
                      disabled={day.mustVisit.length > 0}
                      onChange={(event) => setAlsoNaming(event.target.checked)}
                    />
                  )}
                  label={<Typography variant="body2">Also visit places I name</Typography>}
                />
              )}

              {onBrowsePlaces && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TravelExploreIcon />}
                  onClick={onBrowsePlaces}
                  sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
                >
                  Browse places for this day
                </Button>
              )}

              {showNaming && (
              <PlacePicker
                cityId={cityId}
                allCities
                label="Which place to add?"
                placeholder={
                  day.interests.length > 0 ? 'Search within those kinds' : 'Search any place'}
                visitableOnly
                {...(day.interests.length > 0 ? { categoryKeys: day.interests } : {})}
                value={day.mustVisit}
                onChange={(pois) => onChange({ mustVisit: pois })}
                /* This day's own ends, whichever it inherited them from. */
                reserved={[
                  ...(effectiveStart
                    ? [{ id: effectiveStart.id, why: 'This is where the day starts' }] : []),
                  ...(effectiveEnd
                    ? [{ id: effectiveEnd.id, why: 'This is where the day ends' }] : []),
                ]}
              />
              )}

              {/*
                This day's places, each with its own stay. Blank takes the
                category's, which is what nearly every place should use —
                the list is for saying "except this one".
              */}
              {/* Marked on the map, beside the places named from the list. */}
              {markedStops && markedStops.length > 0 && onMarkedStopsChange && (
                <PinChips
                  pins={markedStops}
                  onChange={onMarkedStopsChange}
                  colour="primary"
                />
              )}

              <StayRows
                places={day.mustVisit}
                pins={[]}
                categories={visitable}
                stopDwellMinutes={day.stopDwellMinutes}
                pinnedFallback={30}
                colourFor={colourFor}
                onPlaceChange={(poiId, minutes) => {
                  const next = { ...day.stopDwellMinutes };
                  if (minutes === null) delete next[poiId];
                  else next[poiId] = minutes;
                  onChange({ stopDwellMinutes: next });
                }}
                onPinChange={() => {}}
                onRemovePlace={(poiId) => {
                  const next = { ...day.stopDwellMinutes };
                  delete next[poiId];
                  onChange({
                    mustVisit: day.mustVisit.filter((p) => p.id !== poiId),
                    stopDwellMinutes: next,
                  });
                }}
                onShowDetails={onShowDetails}
              />
              {(day.sitesMode ?? 'kinds') === 'named' && (
              <>
              {/*
                The planner's own panel, ranked against this day's route.

                Always here and shut until asked for. It used to appear only
                once this day had a route to rank against, so opening the day
                to look for suggestions showed nothing and gave no reason;
                `ready` moves that onto the button, which can say so.
              */}
              <StopRecommendations
                state={recommendations}
                ready={Boolean(draft)}
                cityId={cityId}
                categories={visitable}
                chosenIds={day.mustVisit.map((p) => p.id)}
                onAddStop={(poi) => {
                  if (!day.mustVisit.some((p) => p.id === poi.id)) {
                    onChange({ mustVisit: [...day.mustVisit, poi] });
                  }
                }}
                onShowDetails={onShowDetails}
              />
              </>
              )}

              {/*
                One day longer than the rest, decided beside the stops it
                decides between.

                The trip sets a length for every day, which is right for most
                of them and wrong for the one with a flight at four. The field
                was already carried on the day and sent with its request — it
                simply had nothing to set it. It is not a property of the day
                the way its transport or its emphasis are, so it does not
                belong in that column: it rations these stops, and it is read
                against the list it rations.

                Shown holding the trip's figure until something is typed, at
                which point the label says it has stopped following and offers
                the way back.
              */}
              {/*
                Shown only when there is something for it to ration — the
                same test the one-day form makes, which this did not make at
                all.

                It rations the stops the PLANNER chooses, so with no kinds
                of site in force the day is whatever was named plus the road
                between: a budget rations nothing, and offering one invites
                the reading that a smaller number produces a shorter day.
                Inherited kinds count, since they are in force here too.
              */}
              {(day.sitesMode ?? 'kinds') === 'kinds' && budgetDecides && (
              <Box>
                <DurationField
                  purpose={'How much total time is available for this day, including '
                    + 'both travel and stops. The planner uses this to determine how '
                    + 'many sites can reasonably fit into the day.'}
                  label={day.availableMinutes === null
                    ? 'Time on task (as the trip)'
                    : 'Time on task, this day only'}
                  minutes={day.availableMinutes ?? tripAvailableMinutes}
                  onChange={(minutes) => onChange({ availableMinutes: minutes })}
                  sx={{ width: '100%' }}
                />
                {day.availableMinutes !== null && (
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    underline="hover"
                    onClick={() => onChange({ availableMinutes: null })}
                    sx={{ mt: 0.25 }}
                  >
                    Use the trip’s length
                  </Link>
                )}
              </Box>
              )}
            </Stack>
          </FieldGroup>

          <FieldGroup
            title="Exclusion zones"
            tone="warning"
            count={day.avoid.length + day.avoidCategories.length}
            /* Whose avoidance is in force is the per-day question, so that is
               what the heading says rather than the one-day wording. */
            caption={day.avoid.length > 0 || day.avoidCategories.length > 0
              ? 'Replaces the operation’s avoidance for this day'
              : `Inherited — ${tripAvoidCount} from the operation`}
          >
            <Stack spacing={1.5}>
              <AvoidancePanel
                cityId={cityId}
                onShowDetails={onShowDetails}
                categories={categories}
                filter={avoidFilter}
                onFilterChange={setAvoidFilter}
                avoid={day.avoid}
                onAvoidChange={(pois) => onChange({ avoid: pois })}
                avoidCategories={day.avoidCategories}
                onAvoidCategoriesChange={(keys) => onChange({ avoidCategories: keys })}
                // So the same kind cannot be both visited and avoided — this
                // day's own kinds, since they are what it will be visiting.
                visitingCategories={day.interests}
                avoidDistanceM={day.avoidDistanceM}
                onAvoidDistanceChange={(m) => onChange({ avoidDistanceM: m })}
                colourFor={colourFor}
                avoidDistanceByPoiId={day.avoidDistanceByPoiId}
                avoidDistanceByCategoryKey={day.avoidDistanceByCategoryKey}
                onAvoidDistanceOverride={(scope, key, metres) => {
                  const field = scope === 'poi'
                    ? 'avoidDistanceByPoiId' as const
                    : 'avoidDistanceByCategoryKey' as const;
                  const next = { ...day[field] };
                  if (metres === null) delete next[key];
                  else next[key] = metres;
                  onChange({ [field]: next } as Partial<DayDraft>);
                }}
              />
            </Stack>
          </FieldGroup>

          <FieldGroup
            title="Rest stops"
            caption="Where this day stops to eat, and for how long"
            count={(lunchOn ? 1 : 0) + (coffeeOn ? 1 : 0)}
          >
            <Stack spacing={2}>
              {([
                ['Lunch', 'lunch', lunchOn, tripLunch],
                ['Coffee', 'coffee', coffeeOn, tripCoffee],
              ] as const).map(([label, key, on, inherited]) => (
                <Box key={key}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ minWidth: 54 }}>{label}</Typography>
                    <BreakChoice
                      value={day[key]}
                      inherited={inherited}
                      onChange={(v) => onChange({ [key]: v } as Partial<DayDraft>)}
                    />
                  </Stack>

                  {/*
                    Timing, place and suggestions only when this day is
                    actually having the break — including when it inherits
                    one. Otherwise this would offer to set the hour of
                    something that is not happening.

                    The fields themselves are the one-day form's, so the two
                    ask the same questions in the same words. They had
                    drifted: the hour here was compulsory with no way back to
                    letting the planner choose, the length was written on
                    every keystroke, and the place picker sat beside the
                    suggestions with nothing to say they were one decision.
                  */}
                  {on && (
                    <Box sx={{ pl: 1 }}>
                      <BreakFields
                        label={label}
                        at={day[`${key}At`]}
                        onAtChange={(v) =>
                          onChange({ [`${key}At`]: v })}
                        minutes={day[`${key}Minutes`]}
                        onMinutesChange={(m) =>
                          onChange({ [`${key}Minutes`]: m })}
                        pinned={day[`${key}Pinned`] ?? false}
                        onPinnedChange={(next) =>
                          onChange({ [`${key}Pinned`]: next })}
                        // A day restored without a mode follows what it holds,
                        // the same reading `initialSitesMode` makes.
                        mode={day[`${key}Mode`] ?? (day[`${key}Place`] ? 'named' : 'kinds')}
                        onModeChange={(next) =>
                          onChange({ [`${key}Mode`]: next })}
                        cityId={cityId}
                        categoryKeys={BREAK_CATEGORIES[key]}
                        place={day[`${key}Place`]}
                        onPlaceChange={(poi) =>
                          onChange({ [`${key}Place`]: poi })}
                        placeholder={key === 'lunch' ? 'Search restaurants' : 'Search cafes'}
                        role={key}
                        recommendations={recommendations}
                        canSuggest={Boolean(draft)}
                        /*
                          A day inherits its route from the operation, so
                          "set this day's route" sent somebody looking for a
                          field the day does not have. What is actually
                          missing is where the operation starts.
                        */
                        notReadyLabel="Set where the operation starts"
                        onShowDetails={onShowDetails}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          </FieldGroup>
        </Stack>
      </Collapse>
    </Paper>
  );
}
